import React, { useEffect, useState, useCallback } from "react";
import { FiEye, FiEdit, FiDownload, FiX } from "react-icons/fi";
import ViewAddressModal from "../../components/Address/ViewAddressModal";
import Button from "../../components/Button";
import Pagination from "../../components/Pagination";
import api from "../../api";
import { exportToExcel } from "../../utils/exportToExcel";

export default function SupplierAddress() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // form states
  const [searchTerm, setSearchTerm] = useState("");
  const [addressType, setAddressType] = useState("");
  const [isActive, setIsActive] = useState("");

  // applied filter states
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedAddressType, setAppliedAddressType] = useState("");
  const [appliedIsActive, setAppliedIsActive] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [ordering, setOrdering] = useState("-created_at");

  const ADDRESS_TYPE_CHOICES = [
    { value: "", label: "همه نوع‌ها" },
    { value: "customer", label: "مشتری" },
    { value: "warehouse", label: "انبار" },
  ];

  const ACTIVE_CHOICES = [
    { value: "", label: "همه وضعیت‌ها" },
    { value: "true", label: "فعال" },
    { value: "false", label: "غیرفعال" },
  ];

  const SORT_OPTIONS = [
    { label: "جدیدترین", value: "-created_at" },
    { label: "قدیمی‌ترین", value: "created_at" },
    { label: "نام کاربر (صعودی)", value: "user__full_name" },
    { label: "نام کاربر (نزولی)", value: "-user__full_name" },
  ];

  const ADDRESS_TYPE_TRANSLATIONS = {
    customer: "مشتری",
    warehouse: "انبار",
  };

  const buildAddressQueryParams = useCallback(
    (pageValue = page, noPagination = false) => {
      const params = new URLSearchParams();

      if (!noPagination) {
        params.append("page", pageValue);
        params.append("page_size", pageSize);
      }

      params.append("no_pagination", String(noPagination));

      if (appliedSearchTerm) params.append("search", appliedSearchTerm);
      if (appliedAddressType) params.append("address_type", appliedAddressType);
      if (appliedIsActive !== "") params.append("is_active", appliedIsActive);
      if (ordering) params.append("ordering", ordering);

      return params;
    },
    [page, pageSize, appliedSearchTerm, appliedAddressType, appliedIsActive, ordering]
  );

  const fetchAddresses = useCallback(
    async (pageValue = page, noPagination = false) => {
      setLoading(true);
      setError(null);

      try {
        const params = buildAddressQueryParams(pageValue, noPagination);
        const res = await api.get(`/accounts/admin/addresses/?${params.toString()}`);
        const responseData = res.data;

        if (Array.isArray(responseData)) {
          setAddresses(responseData);
          setTotalCount(responseData.length);
        } else {
          setAddresses(responseData.results || []);
          setTotalCount(responseData.count || 0);
        }
      } catch (err) {
        console.error("Error fetching addresses:", err);
        setError("خطا در دریافت آدرس‌ها");
      } finally {
        setLoading(false);
      }
    },
    [buildAddressQueryParams, page]
  );

  const handleExportExcel = async () => {
    try {
      const params = buildAddressQueryParams(1, true);
      const response = await api.get(`/accounts/admin/addresses/export/?${params.toString()}`);
      const data = response.data?.data || [];

      if (!data.length) {
        alert("داده‌ای برای خروجی وجود ندارد");
        return;
      }

      const excelData = data.map((addr) => ({
        id: addr.id ?? "",
        title: addr.title ?? "",
        full_name: addr.full_name ?? "",
        mobile: addr.mobile ?? "",
        address_type: ADDRESS_TYPE_TRANSLATIONS[addr.address_type] ?? addr.address_type ?? "",
        province_detail: addr.province_detail ?? "",
        city_detail: addr.city_detail ?? "",
        address_text: addr.address_text ?? "",
        postal_code: addr.postal_code ?? "",
        is_active: addr.is_active ?? false,
        is_default: addr.is_default ?? false,
        created_at: addr.created_at ?? "",
      }));

      exportToExcel(excelData, "مدیریت-آدرس‌ها", {
        headers: {
          id: "شناسه",
          title: "عنوان",
          full_name: "نام و نام خانوادگی",
          mobile: "موبایل",
          address_type: "نوع آدرس",
          province_detail: "استان",
          city_detail: "شهر",
          address_text: "آدرس",
          postal_code: "کد پستی",
          is_active: "فعال",
          is_default: "پیش‌فرض",
          created_at: "تاریخ ثبت",
        },
      });
    } catch (err) {
      console.error(err);
      alert("خطا در دریافت اطلاعات آدرس‌ها");
    }
  };

  useEffect(() => {
    fetchAddresses(page, false);
  }, [fetchAddresses, page]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleView = (address) => {
    setSelectedAddress(address);
    setIsViewModalOpen(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearchTerm(searchTerm);
    setAppliedAddressType(addressType);
    setAppliedIsActive(isActive);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setAddressType("");
    setIsActive("");

    setAppliedSearchTerm("");
    setAppliedAddressType("");
    setAppliedIsActive("");
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">مدیریت آدرس‌ها</h1>

        <Button
          variant="secondary"
          onClick={handleExportExcel}
          icon={<FiDownload className="w-5 h-5" />}
        >
          خروجی اکسل
        </Button>
      </div>

      <form onSubmit={handleSearchSubmit} className="w-full mb-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_auto] items-center">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="جستجو بر اساس عنوان، نام، موبایل، استان، شهر یا متن آدرس..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <select
            value={addressType}
            onChange={(e) => setAddressType(e.target.value)}
            className="border rounded-lg p-2.5 pr-[8px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] transition-all"
          >
            {ADDRESS_TYPE_CHOICES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={isActive}
            onChange={(e) => setIsActive(e.target.value)}
            className="border rounded-lg p-2.5 pr-[8px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] transition-all"
          >
            {ACTIVE_CHOICES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2 w-full lg:w-auto">
            <Button type="submit" className="w-full lg:w-auto">
              جستجو
            </Button>

            {(searchTerm || addressType || isActive) && (
              <button
                type="button"
                onClick={handleClearFilters}
                title="پاک کردن فیلترها"
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-all"
              >
                <FiX size={18} />
              </button>
            )}
          </div>
        </div>
      </form>

      <div className="mb-4 flex items-center gap-2 border-b pb-4">
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              setOrdering(option.value);
              setPage(1);
            }}
            className={`px-3 py-1 text-sm rounded-md border transition ${
              ordering === option.value
                ? "bg-[#F5F5F5] text-[#1E3A5F] font-semibold border-[#1E3A5F]"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <p className="mt-2 text-gray-600">در حال دریافت آدرس‌ها...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : addresses.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="mt-4 text-lg font-medium text-gray-900">آدرسی یافت نشد</h3>
            <p className="mt-1 text-gray-500">با تغییر فیلترها دوباره امتحان کنید.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-right">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-4 py-3">عنوان</th>
                  <th className="px-4 py-3">نام و نام خانوادگی</th>
                  <th className="px-4 py-3">نوع آدرس</th>
                  <th className="px-4 py-3">استان</th>
                  <th className="px-4 py-3">شهر</th>
                  <th className="px-4 py-3">آدرس</th>
                  <th className="px-4 py-3">وضعیت</th>
                  <th className="px-4 py-3 text-center">عملیات</th>
                </tr>
              </thead>

              <tbody>
                {addresses.map((addr) => (
                  <tr key={addr.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">{addr.title}</td>
                    <td className="px-4 py-3">{addr.full_name || "-"}</td>
                    <td className="px-4 py-3">
                      {ADDRESS_TYPE_TRANSLATIONS[addr.address_type] || addr.address_type || "-"}
                    </td>
                    <td className="px-4 py-3">{addr.province_detail || "-"}</td>
                    <td className="px-4 py-3">{addr.city_detail || "-"}</td>
                    <td className="px-4 py-3 truncate max-w-xs">
                      {addr.address_text?.length > 50
                        ? `${addr.address_text.substring(0, 50)}...`
                        : addr.address_text || "-"}
                    </td>
                    <td className="px-4 py-3">
                      {addr.is_active ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          فعال
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          غیرفعال
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => handleView(addr)}
                          className="text-gray-600 hover:text-gray-800"
                          title="مشاهده"
                        >
                          <FiEye className="ml-1" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addresses.length > 0 && (
        <div className="mt-6">
          <div className="mb-4 text-center text-sm text-gray-700">
            نمایش <span className="font-medium">{addresses.length}</span> آدرس از{" "}
            <span className="font-medium">
              {new Intl.NumberFormat("fa-IR").format(totalCount)}
            </span>
          </div>

          <div className="flex justify-center">
            <Pagination page={page} setPage={setPage} totalPages={totalPages} />
          </div>
        </div>
      )}

      {isViewModalOpen && (
        <ViewAddressModal
          address={selectedAddress}
          onClose={() => setIsViewModalOpen(false)}
        />
      )}

    </div>
  );
}
